/**
 * Breach warning UX — makes integrity loss legible.
 *
 * Two complementary signals:
 *
 *   1. Per-meteor warning sprite — a red pulsing label projected onto
 *      each meteor that is currently in the dwell window (settled
 *      outside the atmosphere). Player sees exactly WHICH meteor is
 *      counting down to a breach + a "DANGER" tag.
 *
 *   2. Full-width screen banner on the actual breach event — flashes
 *      "ATMOSPHERIC BREACH" at the top of the play area for ~1.2s.
 *      Player understands clearly when (and why) a heart dropped.
 *
 * Pure DOM. World-to-screen projection uses the camera passed in.
 */

import type { Camera } from "three";
import { Vector3 } from "three";

import { t } from "../i18n/index.ts";

export interface BreachWarning {
  /** Update per frame — pass current dwell-meteor positions. */
  setDwellingMeteors(
    meteors: ReadonlyArray<{ id: number; position: { x: number; y: number; z: number } }>,
    nowMs: number,
    viewport: { width: number; height: number },
  ): void;
  /** Fire the atmosphere-boundary breach banner ("⚠ 대기권 돌파!"). */
  flashBreach(): void;
  /** Fire the orbit-escape banner ("🌌 궤도 이탈!") — distinct event. */
  flashEscape(): void;
  detach(): void;
}

export interface BreachWarningOptions {
  host: HTMLElement;
  camera: Camera;
}

const BANNER_DURATION_MS = 1300;

export function createBreachWarning(
  opts: BreachWarningOptions,
): BreachWarning {
  // Per-meteor warning layer.
  const warningLayer = document.createElement("div");
  warningLayer.dataset["testid"] = "breach-warnings";
  warningLayer.className = "pointer-events-none absolute inset-0 overflow-hidden";
  opts.host.appendChild(warningLayer);

  // Banner layer — single DOM element, but text + color swap depending
  // on which event fired (breach vs escape).
  const banner = document.createElement("div");
  banner.dataset["testid"] = "breach-banner";
  banner.className =
    "pointer-events-none absolute left-1/2 top-24 -translate-x-1/2 rounded-md px-6 py-3 font-display text-2xl font-bold text-white shadow-2xl transition-opacity duration-200";
  banner.style.background = "rgba(220, 38, 38, 0.9)";
  banner.textContent = t("breach.atmosphere");
  banner.style.opacity = "0";
  opts.host.appendChild(banner);

  let bannerTimer: number | null = null;
  const projection = new Vector3();
  /** id → DOM element. Recycled across frames. */
  const labels = new Map<number, HTMLDivElement>();

  const acquireLabel = (id: number): HTMLDivElement => {
    let el = labels.get(id);
    if (el) return el;
    el = document.createElement("div");
    el.dataset["testid"] = "breach-warning-label";
    el.dataset["meteorId"] = String(id);
    el.className =
      "absolute -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-red-400 bg-red-500/30 px-2 py-0.5 font-display text-xs font-bold text-red-100";
    el.style.boxShadow = "0 0 18px rgba(255,80,80,0.85)";
    el.textContent = t("breach.danger");
    warningLayer.appendChild(el);
    labels.set(id, el);
    return el;
  };

  const releaseLabel = (id: number): void => {
    const el = labels.get(id);
    if (!el) return;
    el.remove();
    labels.delete(id);
  };

  const setDwellingMeteors = (
    meteors: ReadonlyArray<{ id: number; position: { x: number; y: number; z: number } }>,
    nowMs: number,
    viewport: { width: number; height: number },
  ): void => {
    const seen = new Set<number>();
    for (const m of meteors) {
      seen.add(m.id);
      const el = acquireLabel(m.id);
      projection.set(m.position.x, m.position.y, m.position.z);
      projection.project(opts.camera);
      const screenX = (projection.x * 0.5 + 0.5) * viewport.width;
      const screenY = (-projection.y * 0.5 + 0.5) * viewport.height;
      el.style.left = `${screenX}px`;
      el.style.top = `${screenY}px`;
      // Pulse opacity at ~6 Hz so the label clearly reads as alarm.
      const pulse = 0.55 + 0.45 * Math.sin(nowMs * 0.012);
      el.style.opacity = pulse.toFixed(3);
    }
    // Release labels for meteors that are no longer dwelling.
    for (const id of Array.from(labels.keys())) {
      if (!seen.has(id)) releaseLabel(id);
    }
  };

  const showBanner = (text: string, background: string): void => {
    banner.textContent = text;
    banner.style.background = background;
    banner.style.opacity = "1";
    if (bannerTimer !== null) window.clearTimeout(bannerTimer);
    bannerTimer = window.setTimeout(() => {
      banner.style.opacity = "0";
      bannerTimer = null;
    }, BANNER_DURATION_MS);
  };

  const flashBreach = (): void => {
    // Red — atmosphere boundary stress (meteor stuck just outside atmosphere).
    showBanner(t("breach.atmosphere"), "rgba(220, 38, 38, 0.92)");
  };

  const flashEscape = (): void => {
    // Cyan-violet — orbit escape (meteor flew out of bounds, lost).
    showBanner(t("breach.escape"), "rgba(60, 110, 200, 0.92)");
  };

  const detach = (): void => {
    if (bannerTimer !== null) window.clearTimeout(bannerTimer);
    for (const id of Array.from(labels.keys())) releaseLabel(id);
    warningLayer.remove();
    banner.remove();
  };

  return { setDwellingMeteors, flashBreach, flashEscape, detach };
}

export const __testing = { BANNER_DURATION_MS };
